const objectHash = require('object-hash');

/**
 * Returns every FieldGroup (both order level and item level) in the given Cart or Order.
 *
 * @param {Cart|Order} cartOrOrder
 *
 * @return {FieldGroup[]}
 */
const getAllFieldGroupsFromOrder = (cartOrOrder) => {
    return [
        // Add order level field group.
        cartOrOrder.getOrderLevelFieldGroup(),

        // Add item level field groups.
        ...getAllFieldGroupsFromLines(cartOrOrder.getLines()),
    ];
};

/**
 * Returns every FieldGroup (probably item level as that's all that makes sense) from all the instances in
 * the given array of Order or Cart lines.
 *
 * @param {CartLine[]|OrderLine[]} lines
 *
 * @return {FieldGroup[]}
 */
const getAllFieldGroupsFromLines = (lines) => {
    let fieldGroups = [];

    lines.forEach((line) => {
        line.getItemInstances().forEach((itemInstance) => {
            fieldGroups.push(itemInstance.fields);
        });
    });

    return fieldGroups;
};

/**
 * Returns all the FieldInstances contained within the given array of FieldGroups.
 *
 * @param {FieldGroup[]} groups
 *
 * @returns {FieldInstance[]}
 */
const getAllFieldInstancesFromGroups = (groups) => {
    return groups.reduce((instances, group) => instances.concat(group.instances), []);
};

/**
 * Shortcut to get all the FieldInstances from a cart rather than getting all the FieldGroups and then the instances.
 *
 * @param {Cart} cart
 *
 * @return {FieldInstance[]}
 */
const getAllFieldInstancesFromCart = (cart) => getAllFieldInstancesFromGroups(getAllFieldGroupsFromOrder(cart));

/**
 * Extracts all the field data from the cart and returns a flattened object where they keys are the field
 * instance keys and the values are the user entered value for the field.
 *
 * @param {Cart} cart
 *
 * @return {Object<string, string|number|number[]>}
 */
const getFieldValuesFromCart = (cart) => getFieldValuesFromInstances(getAllFieldInstancesFromCart(cart));

/**
 * @param {CartLine[]|OrderLine[]} lines
 *
 * @return {Object<string, string|number|number[]>}
 */
const getFieldValuesFromLines = (lines) => getFieldValuesFromInstances(
    getAllFieldInstancesFromGroups(
        getAllFieldGroupsFromLines(
            lines
        )
    )
);

/**
 * @param {FieldInstance[]} fieldInstances
 *
 * @return {Object<string, string|number|number[]>}
 */
const getFieldValuesFromInstances = (fieldInstances) => {
    let values = {};

    fieldInstances.forEach((fieldInstance) => {
        let value = fieldInstance.getValue();
        if (value !== null) {
            values[fieldInstance.key] = fieldInstance.getValue();
        }
    });

    return values;
};

/**
 * Set the value of a single field instance from a big object of all values, keyed by the instance keys.
 * Optionally clears the value for all instances that do not have a value present in the given values.
 *
 * @param {Object<string, string>} fieldValues
 * @param {FieldInstance} fieldInstance
 * @param {boolean} [clearExisting=false]
 */
const setValueOfFieldInstance = (fieldValues, fieldInstance, clearExisting = false) => {
    if (fieldValues.hasOwnProperty(fieldInstance.key)) {
        fieldInstance.setValue(fieldValues[fieldInstance.key]);
    } else if (clearExisting) {
        fieldInstance.clearValue();
    }
};

/**
 * @param {Object<string, string>} fieldValues
 * @param {FieldGroup} fieldGroup
 * @param {function} [fieldInstanceFilter] Optional function to filter only certain instances to be modified.
 *                                         See FieldInstanceFilter for ready made examples.
 * @param {boolean} [clearExisting=false]
 */
const setValuesOfFieldGroup = (fieldValues, fieldGroup, fieldInstanceFilter, clearExisting = false) => {
    let instances = fieldGroup.instances;

    if (fieldInstanceFilter) {
        instances = instances.filter(fieldInstanceFilter);
    }

    instances.forEach(
        (fieldInstance) => setValueOfFieldInstance(fieldValues, fieldInstance, clearExisting)
    );
};

/**
 * @param {Object<string, string>} fieldValues
 * @param {Cart} cart
 * @param {function} [fieldInstanceFilter]
 * @param {boolean} [clearExisting=false]
 */
const setFieldValuesOnCart = (fieldValues, cart, fieldInstanceFilter, clearExisting = false) => {
    let fieldGroups = getAllFieldGroupsFromOrder(cart);
    fieldGroups.forEach(
        (fieldGroup) => setValuesOfFieldGroup(fieldValues, fieldGroup, fieldInstanceFilter, clearExisting)
    );
};

/**
 * Returns a string representing the unique combination of values in the given array of FieldInstances.
 * Returns null if there are no instances/
 *
 * @param {FieldInstance[]} fieldInstances
 *
 * @return {string|null}
 */
const getHashOfFieldValues = (fieldInstances) => {
    if (fieldInstances.length < 1) {
        return null;
    }

    const values = fieldInstances
        .filter((fieldInstance) => fieldInstance.hasValue)
        .map((fieldInstance) => ({ ID: fieldInstance.field.ID, value: fieldInstance.getValue() }));

    if (values.length < 1) {
        return null;
    }

    return objectHash(values);
};

module.exports = {
    getAllFieldGroupsFromOrder,
    getAllFieldGroupsFromLines,
    getAllFieldInstancesFromGroups,
    getAllFieldInstancesFromCart,
    getFieldValuesFromCart,
    getHashOfFieldValues,
    getFieldValuesFromLines,
    setFieldValuesOnCart,
    setValueOfFieldInstance,
    setValuesOfFieldGroup,
};
